import { useContext, useState, useEffect, useCallback, useRef } from 'react';
import AuthContext from '../../../contexts/authContext';
import { getAPI, postAPI, putAPI, deleteAPI } from '../../../apis/backend/apiNode';

export const useLogicDevis = () => {
	const { userData } = useContext(AuthContext);
	const [data, setData] = useState([]);
	const [pagination, setPagination] = useState({
		current_page: 1,
		total_pages: 1,
		total: 0,
		perPage: 10,
		sommeTotalTTC: 0
	});
	const [error, setError] = useState(null);
	const [spinner, setSpinner] = useState(false);
	const [perPage, setPerPage] = useState(10);

	// États des filtres
	const [searchTerm, setSearchTerm] = useState('');
	const [companyFilterId, setCompanyFilterId] = useState('');
	const [clientFilterId, setClientFilterId] = useState('');
	const [sourceFilter, setSourceFilter] = useState('');
  	const [motifFilter, setMotifFilter] = useState('');

	const [statutFilter, setStatutFilter] = useState('');
	const [responsableFilter, setResponsableFilter] = useState('');
	const [dateCreationFilter, setDateCreationFilter] = useState('');
	const now = new Date();
	const [startDate, setStartDate] = useState(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]);
	const [endDate, setEndDate] = useState(new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0]);

	// Handlers
	const handleStartDateChange = (date) => setStartDate(date);
	const handleEndDateChange = (date) => setEndDate(date);

	// Référence pour l'AbortController
	const abortControllerRef = useRef(null);

	// 1. Recherche principale (searchDevis) - Logique de requête API
	const searchDevis = useCallback(
		async (term = '', page = 1, limit = 10, signal = null, statutsOverride = undefined) => {
			const queryParams = new URLSearchParams();

			// Always add pagination params
			queryParams.append('page', page.toString());
			queryParams.append('limit', limit.toString());

			const hasSearchTerm = term && term.trim() !== '';

			if (hasSearchTerm) {
				// 🔥 When a search term is present, ONLY use term + pagination
				queryParams.append('term', term.trim());
			} else {
				// No search term → apply all other filters
				const statut = statutsOverride !== undefined ? statutsOverride : statutFilter;
				const companyId = companyFilterId;
				const clientId = clientFilterId;
				const responsable = responsableFilter;
				const dateCreation = dateCreationFilter;
				const start = startDate;
				const end = endDate;

				if (companyId) queryParams.append('companyId', companyId);
				if (clientId) queryParams.append('clientId', clientId);
				if (sourceFilter) queryParams.append('origin', sourceFilter);

				// Multiple status support
				if (Array.isArray(statut) && statut.length > 0) {
					statut.forEach((s) => queryParams.append('statut', s));
				} else if (statut) {
					queryParams.append('statut', statut);
				}

				if (responsable) queryParams.append('responsable', responsable);
				if (motifFilter) queryParams.append('motif', motifFilter);
				if (dateCreation) queryParams.append('dateCreation', dateCreation);
				if (start) queryParams.append('dateDebut', start);
				if (end) queryParams.append('dateFin', end);
			}

			setSpinner(true);
			setError(null);

			try {
				const res = await getAPI('devis', `/recherche?${queryParams.toString()}`, signal);

				if (signal?.aborted) {
					console.log('🔴 Requête annulée:', term);
					return;
				}

				if (res?.success) {
					setData(res.data.devis || []);
					setPagination({
						current_page: res.data.page,
						total_pages: res.data.totalPages,
						total: res.data.total,
						perPage: res.data.limit,
						sommeTotalTTC: res.data.sommeTotalTTC
					});
				} else {
					setError(res?.error || 'Erreur lors de la recherche.');
				}
			} catch (err) {
				if (err.name === 'AbortError') {
					console.log('🔴 Requête annulée (AbortError):', term);
					return;
				}
				console.error('❌ Erreur réseau:', err);
				setError('Erreur réseau lors de la recherche.');
			} finally {
				setSpinner(false);
			}
		},
		[
			companyFilterId,
			clientFilterId,
			statutFilter,
			responsableFilter,
			dateCreationFilter,
			sourceFilter,
			startDate,
			endDate,
			motifFilter
		]
	);

	const relancesDevis = useCallback(
		async (term = '', page = 1, limit = 10, signal = null, statutsOverride = undefined) => {

			const statut = statutsOverride !== undefined ? statutsOverride : statutFilter;

			const companyId = companyFilterId;
			const clientId = clientFilterId;
			// const statut = statutFilter;
			const responsable = responsableFilter;
			const dateCreation = dateCreationFilter;

			const queryParams = new URLSearchParams({
				term: term,
				page: page.toString(),
				limit: limit.toString(),
			});

			if (companyId) queryParams.append('companyId', companyId);
			if (clientId) queryParams.append('clientId', clientId);
			// if (statut) queryParams.append('statut', statut);

			// ✅ Multiple status support
			if (Array.isArray(statut) && statut.length > 0) {
				statut.forEach((s) => queryParams.append('statut', s));
			} else {
				queryParams.append('statut', statut);
			}
			if (responsable) queryParams.append('responsable', responsable);
			if (dateCreation) queryParams.append('dateCreation', dateCreation);

			setSpinner(true);
			setError(null);

			try {
				const res = await getAPI(
					'devis',
					`/relances?${queryParams.toString()}`,
					signal
				);

				if (signal?.aborted) {
					console.log('🔴 Requête annulée:', term);
					return;
				}

				if (res?.success) {
					setData(res.data.devis || []);
					setPagination({
						current_page: res.data.page,
						total_pages: res.data.totalPages,
						total: res.data.total,
						perPage: res.data.limit,
						sommeTotalTTC: res.data.sommeTotalTTC

					});
				} else {
					setError(res?.error || 'Erreur lors de la recherche.');
				}
			} catch (err) {
				if (err.name === 'AbortError') {
					console.log('🔴 Requête annulée (AbortError):', term);
					return;
				}
				console.error('❌ Erreur réseau:', err);
				setError('Erreur réseau lors de la recherche.');
			} finally {
				setSpinner(false);
			}
		},
		[companyFilterId, clientFilterId, statutFilter, responsableFilter, dateCreationFilter]
	);
	const handleStatutFilterChange = useCallback((statut) => {
		setStatutFilter(statut);
	}, []);

	const handleResponsableFilterChange = useCallback((responsable) => {
		setResponsableFilter(responsable);
	}, []);

	const handleDateCreationFilterChange = useCallback((date) => {
		setDateCreationFilter(date);
	}, []);


	// 2. loadData - Wrapper qui utilise searchDevis avec les états actuels
	const loadData = useCallback(
		(page = 1) => {
			// Annuler la requête précédente si elle existe
			if (abortControllerRef.current) {
				console.log('⏹️ Annulation requête précédente');
				abortControllerRef.current.abort();
			}

			// Créer un nouveau AbortController pour cette requête
			abortControllerRef.current = new AbortController();
			const signal = abortControllerRef.current.signal;

			console.log('🔄 Nouvelle requête:', { searchTerm, page, perPage });
			searchDevis(searchTerm, page, perPage, signal);
		},
		[searchDevis, searchTerm, perPage],
	);

	// 3. Handlers de changement de filtres
	const handleSearchTermChange = useCallback((term) => {
		setSearchTerm(term);
	}, []);

	const handleCompanyFilterChange = useCallback((id) => {
		setCompanyFilterId(id);
	}, []);

	const handleClientFilterChange = useCallback((id) => {
		setClientFilterId(id);
	}, []);

	// --- Actions CRUD & Spécifiques ---

	const createDevis = useCallback(async (devisData) => {
		try {
			const result = await postAPI('devis', devisData, '');
			return result || { success: false, error: 'No response from server' };
		} catch (err) {
			console.error('Create Devis error:', err);
			return { success: false, error: err.message };
		}
	}, []);

	const updateDevis = useCallback(async (id, devisData) => {
		try {
			const result = await putAPI('devis', devisData, `/${id}`);
			return result || { success: false, error: 'No response from server' };
		} catch (err) {
			console.error('Update Devis error:', err);
			return { success: false, error: err.message };
		}
	}, []);

	const deleteDevis = useCallback(async (id) => {
		try {
			const result = await deleteAPI('devis', `/${id}`);
			return result || { success: false, error: 'No response from server' };
		} catch (err) {
			console.error('Delete Devis error:', err);
			return { success: false, error: err.message };
		}
	}, []);

	const envoyerDevis = useCallback(async (id) => {
		try {
			const result = await putAPI('devis', {}, `/${id}/envoyer`);
			return result || { success: false, error: 'No response from server' };
		} catch (err) {
			console.error('Send Devis error:', err);
			return { success: false, error: err.message };
		}
	}, []);

	const accepterDevis = useCallback(async (id) => {
		try {
			const result = await putAPI('devis', {}, `/${id}/accepter`);
			return result || { success: false, error: 'No response from server' };
		} catch (err) {
			console.error('Accept Devis error:', err);
			return { success: false, error: err.message };
		}
	}, []);

	const refuserDevis = useCallback(async (id, raison) => {
		try {
			const result = await putAPI('devis', { raison }, `/${id}/refuser`);
			return result || { success: false, error: 'No response from server' };
		} catch (err) {
			console.error('Refuse Devis error:', err);
			return { success: false, error: err.message };
		}
	}, []);

	const transformerEnCommande = useCallback(async (id) => {
		try {
			const result = await putAPI('devis', {}, `/${id}/transformer-commande`);
			return result || { success: false, error: 'No response from server' };
		} catch (err) {
			console.error('Transform Devis error:', err);
			return { success: false, error: err.message };
		}
	}, []);

	const dupliquerDevis = useCallback(async (id) => {
		try {
			const result = await postAPI('devis', {}, `/${id}/dupliquer`);
			return result || { success: false, error: 'No response from server' };
		} catch (err) {
			console.error('Duplicate Devis error:', err);
			return { success: false, error: err.message };
		}
	}, []);

	const genererPDFDevis = useCallback(async (id) => {
		try {
			const result = await getAPI('devis', `/${id}/pdf`);
			return result || { success: false, error: 'No response from server' };
		} catch (err) {
			console.error('Generate PDF Devis error:', err);
			return { success: false, error: err.message };
		}
	}, []);

	const getAvailableTechnicians = useCallback(async () => {
		try {
			const response = await getAPI('employe', '/function/technicien');

			if (response && Array.isArray(response)) {
				return { success: true, data: response };
			}
			return { success: false, error: 'No data received', data: [] };
		} catch (err) {
			console.error('Error loading techniciens:', err);
			return { success: false, error: err.message || 'Erreur réseau', data: [] };
		}
	}, []);

	// 4. Gestion de la pagination
	const handlePageChange = useCallback((newPage) => loadData(newPage), [loadData]);
	const handlePerPageChange = useCallback(
		(newPerPage) => {
			setPerPage(newPerPage);
			loadData(1);
		},
		[loadData],
	);

	// 5. Effet central de DEBOUNCE pour déclencher la recherche
	useEffect(() => {
		// Annuler la requête précédente
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		// Déclenche la recherche à la page 1 avec un délai pour tous les filtres
		const timer = setTimeout(() => {
			loadData(1);
		}, 200);

		return () => {
			clearTimeout(timer);
			// Ne pas annuler ici car cela interfère avec le debounce
		};
	}, [searchTerm, companyFilterId, clientFilterId, perPage, loadData]);

	useEffect(() => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		const timer = setTimeout(() => {
			loadData(1);
		}, 200);

		return () => {
			clearTimeout(timer);
		};
	}, [
		searchTerm,
		companyFilterId,
		clientFilterId,
		statutFilter,
		responsableFilter,
		dateCreationFilter,
		perPage,
		loadData
	]);
	return {
		data,
		pagination,
		error,
		spinner,
		loadData,
		createDevis,
		updateDevis,
		deleteDevis,
		searchDevis,
		relancesDevis,
		handleSearchTermChange,
		handleCompanyFilterChange,
		handleClientFilterChange,
		handleStatutFilterChange,
		handleResponsableFilterChange,
		handleDateCreationFilterChange,
		searchTerm,
		companyFilterId,
		clientFilterId,
		statutFilter,
		responsableFilter,
		dateCreationFilter,
		envoyerDevis,
		accepterDevis,
		refuserDevis,
		transformerEnCommande,
		dupliquerDevis,
		genererPDFDevis,
		handlePageChange,
		handlePerPageChange,
		getAvailableTechnicians,
		setSourceFilter,
		sourceFilter,
		endDate,
		startDate,
		handleEndDateChange,
		handleStartDateChange,
		motifFilter, setMotifFilter
	};
};
